import { useCreateOrderQuoteGuestPreviewLinkMutation } from '@codezo/graphql';
import { toast } from 'sonner';

/**
 * Open a quote in the real guest view, in a real guest session.
 *
 * A fresh link every call. It is single-use and expires in minutes, so there is
 * nothing worth caching and nothing worth copying — the only correct thing to
 * do with one is open it immediately, which is why this hook opens the tab
 * rather than handing the link back.
 *
 * The session it lands in is read-only: everything a guest can see, nothing a
 * guest can do. See `docs/guest-preview-frontend.md` in the backend repo.
 */
export function useGuestPreviewLink(quoteId: string) {
	const [createPreviewLink, { loading }] = useCreateOrderQuoteGuestPreviewLinkMutation();

	const openPreview = async () => {
		if (loading) return;
		/* The tab is opened *before* the await: a `window.open` that lands after
		   an async hop is a popup, and browsers block it. No `noopener` either —
		   it makes `window.open` return null, and the handle is the whole point.
		   The destination is our own client app, so the back-reference is free. */
		const tab = window.open('', '_blank');
		if (!tab) {
			toast.error('Allow pop-ups for this site to open the guest preview');
			return;
		}
		try {
			const { data } = await createPreviewLink({ variables: { orderQuoteId: quoteId } });
			const res = data?.createOrderQuoteGuestPreviewLink;
			if (!res?.success || !res.link) {
				tab.close();
				// The realistic failure is a non-staff account, and the backend
				// says so — don't pre-empt it by guessing from the signed-in email.
				toast.error(res?.message || 'Could not open a guest preview');
				return;
			}
			/* The identity the preview runs as, named at the one moment it is
			   useful. It is a throwaway plus-address, and it is the name that
			   turns up in this quote's viewer list — which is otherwise a small
			   mystery. */
			toast.success('Opening the guest view', {
				description: res.email ? `Previewing as ${res.email}` : undefined,
			});
			tab.location.href = res.link;
		} catch (err) {
			tab.close();
			toast.error(err instanceof Error ? err.message : 'Could not open a guest preview');
		}
	};

	return { openPreview, loading };
}
